import { type KeyboardEvent } from 'react'
import { Link, type To } from 'react-router-dom'

import {
  AccessibleIcon,
  IconArrowRight,
  IconButton,
  IconCaretDown,
  TableCell,
  TableRow,
  Tag
} from '@vtex/shoreline'
import { t } from 'i18next'

import { LogoStore } from '~/components/LogoStore'
import { formatMoneyV2 } from '~/helpers'
import { useShowWalletOriginsColumn } from '~/modules/retailMedia/hooks/useShowWalletOrigins'

import { WalletBalanceOrigin } from '../WalletBalanceOrigin'
import { mapCheckingAccountOrigins } from '../WalletBalanceOrigin/mapOrigins'
import type { WalletBalanceOriginRow } from '../WalletBalanceOrigin/types'
import CheckingAccountRechargeRow from './components/Recharge'
import { getOriginsSummary } from './getOriginsSummary'

import styles from './styles.module.css'

const isRowActivateKey = (key: string) => key === 'Enter' || key === ' '

const getMainRowClassName = (canExpand: boolean, isOpen: boolean) =>
  [
    styles.mainRow,
    canExpand ? styles.mainRowExpandable : '',
    isOpen ? styles.mainRowOpen : ''
  ]
    .filter(Boolean)
    .join(' ')

const OriginSummaryTags = ({
  piCount,
  hasPreexisting
}: {
  piCount: number
  hasPreexisting: boolean
}) => (
  <span className={styles.originTags}>
    {piCount > 0 ? (
      <Tag variant="secondary" color="blue">
        {t('rm:wallet.list.origins.pis', { count: piCount })}
      </Tag>
    ) : null}
    {hasPreexisting ? (
      <Tag variant="secondary" color="gray">
        {t('rm:wallet.list.origins.preexisting.alone')}
      </Tag>
    ) : null}
  </span>
)

const CheckingAccountChevronCell = ({
  canExpand,
  isOpen,
  expandOriginsLabel,
  expandPanelId,
  onToggle
}: {
  canExpand: boolean
  isOpen: boolean
  expandOriginsLabel: string
  expandPanelId: string
  onToggle: () => void
}) => (
  <TableCell className={styles.chevronCell}>
    {canExpand ? (
      <IconButton
        variant="tertiary"
        label={expandOriginsLabel}
        aria-expanded={isOpen}
        aria-controls={expandPanelId}
        className={isOpen ? styles.chevronOpen : undefined}
        onClick={event => {
          event.stopPropagation()
          onToggle()
        }}
      >
        <IconCaretDown />
      </IconButton>
    ) : null}
  </TableCell>
)

const CheckingAccountOriginsCell = ({
  originsSummary
}: {
  originsSummary: ReturnType<typeof getOriginsSummary>
}) => (
  <TableCell className={styles.originsCell}>
    {originsSummary ? (
      <OriginSummaryTags
        piCount={originsSummary.piCount}
        hasPreexisting={originsSummary.hasPreexisting}
      />
    ) : (
      '—'
    )}
  </TableCell>
)

const CheckingAccountOriginsPanel = ({
  isVisible,
  expandPanelId,
  originRows,
  detailTo
}: {
  isVisible: boolean
  expandPanelId: string
  originRows: WalletBalanceOriginRow[]
  detailTo: To
}) => (
  <div id={expandPanelId} hidden={!isVisible} className={styles.expandPanel}>
    {isVisible ? (
      <WalletBalanceOrigin origins={originRows} walletDetailTo={detailTo} />
    ) : null}
  </div>
)

type CheckingAccountTableRowProps = {
  row: GetCheckingAccountWithAccountInfoResponse
  accountKey: 'advertiser' | 'publisher'
  showRechargeColumn: boolean
  isOpen: boolean
  onToggle: () => void
}

export const CheckingAccountTableRow = ({
  row,
  accountKey,
  showRechargeColumn,
  isOpen,
  onToggle
}: CheckingAccountTableRowProps) => {
  const showOriginsColumn = useShowWalletOriginsColumn()
  const account = row as GetCheckingAccountWithAccountInfoResponse &
    Record<string, unknown>
  const accountName =
    (account[`${accountKey}_name`] as string) || t('rm:account.name.empty')
  const accountId = account[`${accountKey}_id`] as string
  const logo = account[`${accountKey}_account_logo`] as string
  const theme = account[`${accountKey}_account_theme`] as
    | { header_color?: string }
    | undefined
  const bgColor = theme?.header_color || 'white'
  const balanceNumber = Number(row.balance || 0)
  const originRows = mapCheckingAccountOrigins(row.origins)
  const originsSummary = getOriginsSummary(originRows)
  const canExpand = showOriginsColumn && originRows.length > 0
  const isExpanded = canExpand && isOpen
  const detailTo = `/retail-media/wallet/${row.id}/movimentations?rmid=${accountId}`
  const viewWalletLabel = t('rm:wallet.list.viewWalletOf', {
    name: accountName
  })
  const expandOriginsLabel = t('rm:wallet.list.expandOrigins')
  const expandPanelId = `wallet-origins-panel-${row.id}`

  const handleRowActivate = () => {
    if (canExpand) onToggle()
  }

  const handleRowKeyDown = (event: KeyboardEvent) => {
    if (!canExpand || !isRowActivateKey(event.key)) return
    event.preventDefault()
    onToggle()
  }

  return (
    <div className={styles.rowGroup}>
      <TableRow
        dimOnHover={false}
        expanded={isExpanded}
        className={getMainRowClassName(canExpand, isExpanded)}
        aria-expanded={canExpand ? isOpen : undefined}
        onClick={handleRowActivate}
        onKeyDown={handleRowKeyDown}
        tabIndex={canExpand ? 0 : undefined}
      >
        {showOriginsColumn ? (
          <CheckingAccountChevronCell
            canExpand={canExpand}
            isOpen={isOpen}
            expandOriginsLabel={expandOriginsLabel}
            expandPanelId={expandPanelId}
            onToggle={onToggle}
          />
        ) : null}
        <TableCell className={styles.accountCellWrapper}>
          <Link
            to={detailTo}
            className={styles.accountCell}
            onClick={event => event.stopPropagation()}
          >
            <LogoStore
              backgroundLogo={bgColor}
              linkLogo={logo}
              alt={accountName}
              size="small"
            />
            <span className={styles.accountName}>{accountName}</span>
          </Link>
        </TableCell>
        <TableCell className={styles.balanceCell}>
          <span
            className={
              balanceNumber === 0
                ? `${styles.balance} ${styles.balanceZero}`
                : styles.balance
            }
          >
            {formatMoneyV2({ value: balanceNumber })}
          </span>
        </TableCell>
        {showOriginsColumn ? (
          <CheckingAccountOriginsCell originsSummary={originsSummary} />
        ) : null}
        {showRechargeColumn ? (
          <TableCell onClick={event => event.stopPropagation()}>
            <CheckingAccountRechargeRow data={row} />
          </TableCell>
        ) : null}
        <TableCell className={styles.actionCell}>
          <IconButton label={viewWalletLabel} variant="secondary" asChild>
            <Link
              to={detailTo}
              aria-label={viewWalletLabel}
              onClick={event => event.stopPropagation()}
            >
              <AccessibleIcon label={viewWalletLabel}>
                <IconArrowRight />
              </AccessibleIcon>
            </Link>
          </IconButton>
        </TableCell>
      </TableRow>
      <CheckingAccountOriginsPanel
        isVisible={isExpanded}
        expandPanelId={expandPanelId}
        originRows={originRows}
        detailTo={detailTo}
      />
    </div>
  )
}
